import type { HTTPMethod } from 'h3';

import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { useFetch, useRuntimeConfig } from '#app';

import { getRequestContextHeaders } from '../utils/RequestContext/RequestContext';

// this function is a wrapper for Orval and uses the useFetch hook

// We can pass the same options as the useFetch hook
// https://nuxt.com/docs/api/composables/use-fetch (UseFetchOptions)

export const useApiFetch = <T>(
  request: { url: string; method: HTTPMethod; params?: unknown; headers?: unknown; data?: unknown } | string,
  // any added because of issue with complex type generation with Orval
  opts?: Parameters<typeof useFetch<T>>[1] | any, // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
  // Orval expects an object and not a string, we can pass both as parameters
  const url = typeof request === 'string' ? request : request.url;
  const method = typeof request === 'string' ? opts?.method || 'get' : request.method;
  const body = typeof request === 'string' ? opts?.body : request.data;
  const {
    public: { DEBUG_REQUESTS },
  } = useRuntimeConfig();

  return useFetch<T>(url, {
    deep: false,
    ...(body ? { body: snakecaseKeys(body as Record<string, unknown>, { deep: true }) } : {}),
    ...((typeof request !== 'string' &&
      request?.params && {
        params: snakecaseKeys(request?.params as Record<string, unknown>, { exclude: ['includes[]'] }),
      }) as Record<string, unknown>),
    method,
    onRequest({ request }) {
      if (import.meta.dev && DEBUG_REQUESTS) {
        const httpMethod = (method as string)?.toUpperCase?.() || 'GET';
        console.log(`🔵 REQUEST useApiFetch() → ${httpMethod} ${request.toString()}`);
      }
    },
    onResponse({ response }) {
      if (import.meta.dev && DEBUG_REQUESTS) {
        console.log(`🟢 RESPONSE useApiFetch() ← ${response.status}`);
      }
    },
    // any added because of issue with complex type generation with Orval
    transform: (data) => camelcaseKeys(data as readonly any[], { deep: true }), // eslint-disable-line @typescript-eslint/no-explicit-any
    getCachedData(key, nuxtApp, { cause }) {
      if (cause === 'refresh:hook') {
        return undefined;
      }

      if (method?.toUpperCase() !== 'GET' && !url.includes('template-assets')) return undefined;
      return nuxtApp.payload.data[key] || undefined;
    },
    /* This is commented out due to a suspected bug that started causing 404s on the thank you page.
    @see: https://app.bugsnag.com/dpg-media/re-kiosk/errors/659e7f097f755d00084daf80
    getCachedData(key) {
      // Prevent the following urls from being cached by useFetch's built-in caching mechanism.
      const cachingUrlBlacklist = ['/front-office/v2/cart/', '/front-office/v2/orders'];
      const shouldNotCacheRequest = cachingUrlBlacklist.some((key) => url.startsWith(key));
      if (shouldNotCacheRequest) return;
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key];
    },
    */
    ...opts,
    headers: {
      ...getRequestContextHeaders(),
      // @info keep this in order as the last item, so it can be used as override!
      ...opts?.headers,
    },
  });
};
